import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, ArrowRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import PageHero from "@/components/PageHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
    { v: "developer", l: "Developer" },
    { v: "node_operator", l: "Node Operator" },
    { v: "gpu_provider", l: "GPU Provider" },
    { v: "storage_provider", l: "Storage Provider" },
    { v: "validator", l: "Validator" },
    { v: "investor", l: "Investor" },
    { v: "partner", l: "Enterprise Partner" },
    { v: "community", l: "Community Member" },
];

export default function ContactPage() {
    const [params] = useSearchParams();
    const initialRole = params.get("role") || "developer";
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        role: initialRole,
        location: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        setForm((f) => ({ ...f, role: initialRole }));
    }, [initialRole]);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.email || !form.email.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/waitlist`, {
                email: form.email,
                role: form.role,
                source: `contact · ${form.name || "anon"} · ${form.company || ""} · ${form.location || ""} · ${form.message || ""}`.slice(0, 300),
            });
            setDone(true);
            toast.success("Thank you. The DeCloudX team will be in touch.");
        } catch (err) {
            toast.error("Submission failed. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageShell>
            <PageHero
                eyebrow="Join Network"
                title="Join the DeCloudX"
                accent="Network."
                sub="Tell us who you are and how you want to participate. Our team reviews every submission personally."
            />

            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <form onSubmit={onSubmit} className="surface-strong rounded-2xl p-6 md:p-8 space-y-5" data-testid="contact-form">
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Name" name="name" value={form.name} onChange={onChange} placeholder="Ada Lovelace" />
                            <Field label="Email" name="email" type="email" required value={form.email} onChange={onChange} placeholder="ada@protocol.dev" />
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Company / Org" name="company" value={form.company} onChange={onChange} placeholder="Optional" />
                            <Field label="Location" name="location" value={form.location} onChange={onChange} placeholder="City, Country" />
                        </div>
                        <div>
                            <Label>Role</Label>
                            <select name="role" value={form.role} onChange={onChange} className="w-full mt-1.5 bg-card border border-line rounded-xl px-4 py-3 text-ink focus:outline-none focus:border-brand-blue text-sm" data-testid="contact-role">
                                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Message</Label>
                            <textarea name="message" value={form.message} onChange={onChange} rows={4} placeholder="Tell us about your project, capacity, or interest." className="w-full mt-1.5 bg-card border border-line rounded-xl px-4 py-3 text-ink focus:outline-none focus:border-brand-blue text-sm font-mono leading-relaxed" data-testid="contact-message" />
                        </div>

                        <div className="pt-2 flex flex-wrap items-center justify-between gap-3">
                            <p className="text-xs text-faint-ink">We respond within 1–3 business days. No spam.</p>
                            <button type="submit" disabled={loading || done} className="btn-primary justify-center disabled:opacity-60 disabled:hover:scale-100" data-testid="contact-submit">
                                {loading ? <Loader2 size={16} className="animate-spin" /> : done ? "Submitted" : (<>Submit Interest <ArrowRight size={15} strokeWidth={2} /></>)}
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        </PageShell>
    );
}

function Label({ children }) {
    return <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">{children}</div>;
}

function Field({ label, name, type = "text", value, onChange, placeholder, required }) {
    return (
        <div>
            <Label>{label}</Label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                className="w-full mt-1.5 bg-card border border-line rounded-xl px-4 py-3 text-ink placeholder:text-faint-ink focus:outline-none focus:border-brand-blue text-sm"
                data-testid={`contact-${name}`}
            />
        </div>
    );
}
